import { Role } from '@ktypes/enums';
import { MapCategory, NavItem, NavItemType, ToolkitTool } from '@ktypes/models';

export class Constants {
  static dateFormatForLocal = "yyyy-MM-dd'T'HH:mm:ss.SSSXXX";
  static ssoLoginUrlTemplate = `$authHost/oauth2/authorize?identity_provider=$provider&redirect_uri=$kumanuHost&response_type=$response_type&client_id=$clientId&prompt=select_account&state=$state&scope=email%20openid%20phone%20profile`;
  static ssoLogoutUrlTemplate = '$authHost/logout?client_id=$clientId&logout_uri=$kumanuLogout';
  static ssoLogoutAndReloginUrlTemplate =
    '$authHost/logout?identity_provider=$provider&redirect_uri=$kumanuHost&response_type=$response_type&client_id=$clientId&prompt=select_account&state=$state&scope=email%20openid%20phone%20profile';

  static supportedBrowsers = [
    {
      displayName: 'Edge',
      name: 'MS-Edge-Chromium',
      version: 80,
      os: ['Windows', 'Mac'],
      matchExact: false,
      recommend: true,
      icon: '/assets/icons/browsers/edge.png',
      url: 'https://www.microsoft.com/en-us/edge/',
    },
    {
      displayName: 'Edge',
      name: 'MS-Edge',
      version: 80,
      os: ['Android', 'iOS'],
      matchExact: false,
      recommend: true,
      icon: '/assets/icons/browsers/edge.png',
      url: 'https://www.microsoft.com/en-us/edge/',
    },
    {
      displayName: 'Chrome',
      name: 'Chrome',
      version: 0,
      os: [],
      matchExact: false,
      recommend: true,
      icon: '/assets/icons/browsers/chrome.png',
      url: 'https://www.google.com/chrome/',
    },
    {
      displayName: 'Firefox',
      name: 'Firefox',
      version: 0,
      os: [],
      matchExact: false,
      recommend: true,
      icon: '/assets/icons/browsers/firefox.png',
      url: 'https://www.mozilla.org/en-US/firefox/new/',
    },
    {
      displayName: 'Safari',
      name: 'Safari',
      version: 12,
      os: ['Mac', 'iOS'],
      matchExact: false,
      recommend: true,
      icon: '/assets/icons/browsers/safari.png',
      url: 'https://support.apple.com/downloads/safari',
    },
  ];

  static navItemList: NavItem[] = [
    {
      link: '/dashboard',
      icon: '/assets/icons/icon_navigation_dashboard.svg',
      title: 'Dashboard',
      displayOrder: { insightful: { side: 10 } },
      mapCategory: MapCategory.main,
      external: false,
      type: NavItemType.standard,
    },
    {
      link: '/reports',
      icon: '/assets/icons/icon_navigation_reports.svg',
      title: 'Reports',
      displayOrder: { insightful: { side: 20 } },
      mapCategory: MapCategory.main,
      external: false,
      type: NavItemType.standard,
      notRoles: [Role.INSIGHTFUL_ADMIN],
    },
    {
      link: '/admin',
      icon: '/assets/icons/admin-badge.svg',
      title: 'Admin',
      displayOrder: { insightful: { side: 20 } },
      mapCategory: MapCategory.admin,
      external: false,
      type: NavItemType.insightfulAdmin,
      roles: [Role.INSIGHTFUL_ADMIN],
      children: [
        {
          link: '/admin/overview',
          icon: '/assets/icons/light-bulb-outline.svg',
          title: 'Overview',
          displayOrder: { insightful: { side: 30 } },
          mapCategory: MapCategory.admin,
          external: false,
          type: NavItemType.insightfulAdmin,
          roles: [Role.INSIGHTFUL_ADMIN],
          hideFromNav: true,
        },
        {
          link: '/admin/toolkit',
          icon: '/assets/icons/notes.svg',
          title: 'Toolkit',
          displayOrder: { insightful: { side: 32 } },
          mapCategory: MapCategory.admin,
          external: false,
          type: NavItemType.insightfulAdmin,
          roles: [Role.INSIGHTFUL_ADMIN],
          hideFromNav: true,
        },
        {
          link: '/admin/settings',
          icon: '/assets/icons/gear.svg',
          title: 'Settings',
          displayOrder: { insightful: { side: 34 } },
          mapCategory: MapCategory.admin,
          external: false,
          type: NavItemType.insightfulAdmin,
          roles: [Role.INSIGHTFUL_ADMIN],
          hideFromNav: true,
        },
      ],
    },
    {
      link: '/kumanu-admin',
      icon: '/assets/icons/admin-badge.svg',
      title: 'Kumanu Admin',
      displayOrder: { insightful: { side: 40 } },
      mapCategory: MapCategory.main,
      external: false,
      type: NavItemType.elevatedAccess,
      roles: [Role.KUMANU_INSIGHTFUL_ADMIN],
    },
    {
      link: '/site-map',
      icon: '/assets/icons/globe.svg',
      title: 'Site Map',
      displayOrder: { insightful: { side: 50 } },
      mapCategory: MapCategory.main,
      external: false,
      hideFromMap: true,
      type: NavItemType.standard,
    },
  ];

  static smbEmailInvite = {
    subject: 'Purposeful App Invitation: Boost Energy, Reduce Stress and Build Healthy Habits',
    body: `Greetings!

I'm excited to invite you to Purposeful, a unique and powerful wellbeing app we're now providing free for everyone in our organization.

Purposeful is designed to help you feel your best, be your best, and thrive — in just a few minutes each day. It's full of tools and resources to help you have more energy, reduce stress, build healthy habits, and so much more.

Follow the link below to get started with Purposeful today. It only takes 5 minutes to sign up.

{sign_up_link}

I've started using Purposeful and it's already helping me feel more energized, keep calm under pressure, and stay focused on what matters most. I'm confident you'll find it as valuable as I have.

If you have any questions, please feel free to reach out.

Sincerely,

{nickname}
`,
  };

  static toolkitItems: Partial<ToolkitTool>[] = [
    {
      title: 'Launching Purposeful in Your Organization',
      dateUploaded: '2025-01-07',
      fileType: 'application/pdf',
      fileName: 'Launching Purposeful in Your Organization.pdf',
      url: '/toolkit/Launching Purposeful in Your Organization.pdf',
    },
    {
      title: 'Power of Purpose Editable Flyer',
      dateUploaded: '2025-01-09',
      fileType: 'application/pdf',
      fileName: 'Power of Purpose editable.pdf',
      url: '/toolkit/Power of Purpose editable.pdf',
    },
    {
      title: 'Sample Email - Purposeful Helps with Focus',
      dateUploaded: '2025-01-12',
      fileType: 'application/msword',
      fileName: 'Sample Purposeful Promotional Email  1.docx',
      url: '/toolkit/Sample Purposeful Promotional Email  1.docx',
    },
    {
      title: 'Sample Email - Purposeful Resource Center',
      dateUploaded: '2025-01-13',
      fileType: 'application/msword',
      fileName: 'Sample Purposeful Promotional Email  2.docx',
      url: '/toolkit/Sample Purposeful Promotional Email  2.docx',
    },
    {
      title: 'Sample Email - Purposeful Tip of the Day',
      dateUploaded: '2025-01-15',
      fileType: 'application/msword',
      fileName: 'Sample Purposeful Promotional Email  3.docx',
      url: '/toolkit/Sample Purposeful Promotional Email  3.docx',
    },
  ];
}

export enum OauthResponseTypes {
  code = 'code',
  token = 'token',
}
